import {
  getApp,
  getApps,
  initializeApp,
  type FirebaseOptions,
} from "firebase/app";
import {
  createUserWithEmailAndPassword,
  getAuth,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from "firebase/auth";

import { apiClient } from "@/api/client";

import { AuthError, type AuthClient, type AuthSession } from "./client";

type SessionListener = (session: AuthSession | null) => void;

/** Longest `ready()` waits for the restored session to be published. */
const READY_BACKSTOP_MS = 5_000;

/**
 * Extracts the `roles` custom claim (PRD §6.4) as slugs.
 *
 * Claims are attacker-visible and only as fresh as the token, so this is
 * deliberately defensive: anything that isn't an array of strings — most
 * importantly the claim being absent — yields no roles. Failing to an empty set
 * means an unexpected claim shape hides admin UI rather than offering it.
 */
function readRoles(claims: Record<string, unknown>): readonly string[] {
  const raw = claims.roles;
  if (!Array.isArray(raw)) return [];
  return raw.filter((r): r is string => typeof r === "string");
}

export class FirebaseAuthClient implements AuthClient {
  private readonly auth: Auth;
  private currentSession: AuthSession | null = null;
  private readonly listeners = new Set<SessionListener>();
  private readonly readyPromise: Promise<void>;

  constructor(config: FirebaseOptions) {
    const app = getApps().length ? getApp() : initializeApp(config);
    this.auth = getAuth(app);

    // onIdTokenChanged fires on sign-in, sign-out, AND token refresh
    // (<1h cadence). Token refresh is why this listener exists: without
    // it, authenticated API calls would start failing with 401 after
    // the first hour of a session.
    let markPublished!: () => void;
    const firstPublished = new Promise<void>((resolve) => {
      markPublished = resolve;
    });
    onIdTokenChanged(this.auth, async (user) => {
      try {
        this.currentSession = user ? await this.sessionFromUser(user) : null;
        for (const cb of this.listeners) cb(this.currentSession);
      } finally {
        markPublished();
      }
    });

    // `authStateReady` resolves when Firebase knows the user, but the
    // session reaches listeners only after `sessionFromUser` awaits the
    // token. Boot reads the store straight after `ready`, so resolving
    // earlier would make a signed-in reload start as nobody: consent would
    // resolve for an anonymous visitor and a device's analytics answer would
    // run on the account's page. The backstop keeps a session that never builds
    // from holding the app unmounted.
    this.readyPromise = this.auth
      .authStateReady()
      .then(() =>
        Promise.race([
          firstPublished,
          new Promise<void>((resolve) => setTimeout(resolve, READY_BACKSTOP_MS)),
        ]),
      );
  }

  ready(): Promise<void> {
    return this.readyPromise;
  }

  onSessionChanged(cb: SessionListener): () => void {
    this.listeners.add(cb);
    // Fire once immediately so new subscribers don't miss the current
    // state. Callers shouldn't care whether the listener was registered
    // before or after the initial auth-state resolution.
    cb(this.currentSession);
    return () => {
      this.listeners.delete(cb);
    };
  }

  getCurrentSession(): AuthSession | null {
    return this.currentSession;
  }

  async register(email: string, password: string): Promise<AuthSession> {
    // Pre-validate client-side so mock semantics are preserved exactly.
    // Firebase would return auth/missing-password / auth/invalid-email
    // for these cases, but we normalise to the mock's error taxonomy
    // so the UI renders one set of errors for both clients.
    this.validateInput(email, password);
    try {
      const cred = await createUserWithEmailAndPassword(
        this.auth,
        email.trim().toLowerCase(),
        password,
      );
      // Fire-and-forget: verification mail must NOT block registration.
      // An account whose verification mail is refused must still be
      // created — its owner can ask for another link from /verify, and
      // could not if registration had failed. The API mints the link with
      // the Admin SDK and sends it through our own Resend client (rl-4cu),
      // so a `550` is an ERROR log and a Slack alert in our process rather
      // than a 200 from `accounts:sendOobCode` for a message the relay has
      // already refused.
      //
      // **THE REQUEST IS NOT MADE HERE, AND THAT IS THE WHOLE POINT.** Our
      // `users` row is created by `POST /register`, which `RegisterView`
      // calls AFTER this method returns (`rl-757.18`). So a verification
      // request fired from here always arrives before the row exists and is
      // refused with `unregistered_account` -- 403, swallowed by a
      // fire-and-forget `.catch`, and no mail.
      //
      // It lives at the end of `RegisterView.handleSubmit`, after
      // `/register` succeeds. Do not move it here: this method cannot know
      // whether the account has been registered with US, only that Firebase
      // has one.
      return await this.sessionFromUser(cred.user);
    } catch (e) {
      throw this.mapError(e);
    }
  }

  async login(email: string, password: string): Promise<AuthSession> {
    this.validateInput(email, password);
    try {
      const cred = await signInWithEmailAndPassword(
        this.auth,
        email.trim().toLowerCase(),
        password,
      );
      return await this.sessionFromUser(cred.user);
    } catch (e) {
      throw this.mapError(e);
    }
  }

  async signOut(): Promise<void> {
    await firebaseSignOut(this.auth);
  }

  async resendVerificationEmail(): Promise<void> {
    // Signed-out guard. The endpoint is authenticated, so without a
    // session there is no request worth making, and this is the error code
    // the UI already renders for it.
    const user = this.auth.currentUser;
    if (!user) {
      throw new AuthError(
        "invalid-credentials",
        "Sign in before requesting a verification email",
      );
    }
    // rl-4cu: our own endpoint, not the client SDK. A rejection travels as
    // the ApiError it is. Every surface offering this control already
    // writes its own sentence for a failure (VerifyView,
    // GettingStartedCard, AccountView), so putting an API status through
    // `mapError`'s Firebase code table would discard the status and
    // announce "Authentication failed" for a mail outage.
    await apiClient.sendVerificationEmail();
  }

  async refreshSession(): Promise<AuthSession | null> {
    const user = this.auth.currentUser;
    if (!user) {
      this.currentSession = null;
      return null;
    }
    // reload() refreshes the user profile (incl. emailVerified after the
    // user clicks the verification link); getIdToken(true) force-mints a
    // new ID token so its email_verified claim matches the refreshed
    // profile, instead of returning the stale cached token. Rebuild the
    // session explicitly and notify listeners so the store updates
    // deterministically (the force-refresh also fires onIdTokenChanged,
    // which is idempotent — it sets the same values).
    await user.reload();
    await user.getIdToken(true);
    this.currentSession = await this.sessionFromUser(user);
    for (const cb of this.listeners) cb(this.currentSession);
    return this.currentSession;
  }

  async requestPasswordReset(email: string): Promise<void> {
    // NO SWALLOW, deliberately. An unregistered address must be
    // indistinguishable from a registered one, and THAT PROPERTY LIVES IN
    // THE SERVER ROUTE: POST /auth/password-reset answers a byte-identical
    // 200 {"outcome":"accepted"} — and in the same time, because it
    // replies before attempting the send — for a registered address, an
    // unregistered one, a throttled caller and a failed send, and its
    // response model cannot express a distinction (rl-4cu). Nothing that
    // reaches this `await` can be an answer about the address.
    //
    // So a rejection here is an OUTAGE: the API unreachable, or broken.
    // Do not add a swallow. It would protect nothing the server is not
    // already protecting, and would hide precisely the failure that
    // matters — refused reset mail that every green gate and every calm
    // UI agrees has been sent. Callers render
    // their own neutral copy (ResetPasswordView, AccountView); none of
    // them may say anything address-specific.
    await apiClient.requestPasswordReset(email.trim().toLowerCase());
  }

  private async sessionFromUser(user: User): Promise<AuthSession> {
    // getIdTokenResult() returns the same cached token as getIdToken()
    // plus its decoded claims, so reading roles costs no extra round
    // trip and stays consistent with the token we hand the API.
    const result = await user.getIdTokenResult();
    return {
      user: {
        id: user.uid,
        email: user.email ?? "",
        emailVerified: user.emailVerified,
      },
      token: result.token,
      roles: readRoles(result.claims),
    };
  }

  private validateInput(email: string, password: string): void {
    if (!email.trim() || !password) {
      throw new AuthError("invalid-input", "Email and password are required");
    }
    if (password.length < 8) {
      throw new AuthError(
        "weak-password",
        "Password must be at least 8 characters",
      );
    }
  }

  private mapError(e: unknown): AuthError {
    const code = (e as { code?: string }).code ?? "";
    switch (code) {
      case "auth/email-already-in-use":
        return new AuthError("email-already-in-use", "Email already in use");
      case "auth/weak-password":
        return new AuthError(
          "weak-password",
          "Password must be at least 8 characters",
        );
      case "auth/invalid-email":
        return new AuthError("invalid-input", "Enter a valid email address");
      // Consolidate all "bad credentials" failure modes into one error
      // code so the UI doesn't accidentally leak whether an account
      // exists. user-disabled is included for the same reason.
      case "auth/invalid-credential":
      case "auth/wrong-password":
      case "auth/user-not-found":
      case "auth/user-disabled":
        return new AuthError(
          "invalid-credentials",
          "Invalid email or password",
        );
      case "auth/too-many-requests":
        return new AuthError(
          "rate-limited",
          "Too many attempts. Try again in a moment.",
        );
      case "auth/network-request-failed":
        return new AuthError(
          "network",
          "Network error. Check your connection.",
        );
      case "auth/operation-not-allowed":
        // Identity Platform misconfiguration — email/password provider
        // not enabled. Log at error level so ops notices.
        console.error(
          "firebase: auth/operation-not-allowed — enable email/password sign-in in Identity Platform",
        );
        return new AuthError(
          "unknown",
          "Authentication failed. Please try again.",
        );
      default:
        console.warn("firebase: unmapped auth error", code, e);
        return new AuthError(
          "unknown",
          "Authentication failed. Please try again.",
        );
    }
  }
}
